import { HttpClient } from "@angular/common/http";
import { Injectable } from "@angular/core";
import { getOptionsQueryParams } from "@app/class/Utilities";
import { IPeripheral } from "@app/interfaces/IPeripheral";
import { IPlotEvent } from "@app/interfaces/IPlot";
import { IVehicle } from "@app/interfaces/IVehicle";
import { IVehiclePeripheralMapping } from "@app/interfaces/IVehiclePeripheralMapping";
import { IVideoChannelQueryOptions } from "@app/main/modules/cameras/_services/camera-channels.service";
import { DateService, DateUnit } from "@app/services/date.service";
import { ProductService } from "@app/services/product.service";
import { CameraLocation } from "../enums/CameraLocation";
import { VideoStatus } from "../enums/VideoStatus";
import { VideoLabelIcon } from "../enums/video-label-icon";
import { FtCloudStreamType, ICameraChannel, IVideoStream, UrlRequired } from "../models/ICameraChannel";
import { ICameraEventPeriod } from "../models/ICameraEventPeriod";
import { IVideoEntry } from "../models/IVideoEntry";
import { IVideoExtensionData } from "../models/IVideoExtensionData";
import { IVideoRequest } from "../models/IVideoRequest";
import { IVideoRequestRestrictionResponse } from "../models/IVideoRequestRestrictionResponse";
import { CameraEventThreshold, CameraEventThresholds } from "../models/camera-event-thresholds";
import { CameraHistoricalStreamOptions } from "../models/CameraHistoricalStreamOptions";
import { lastValueFrom } from "rxjs";

@Injectable({
    providedIn: 'root'
})
export class CameraService {
    constructor(
        private readonly httpClient: HttpClient,
        private readonly dateService: DateService,
        private readonly productService: ProductService
    ) {
    }

    getIconForEvent(eventLabel: string): string {
        if (!eventLabel) {
            return VideoLabelIcon.Video;
        }

        eventLabel = eventLabel.toLocaleLowerCase();

        if (eventLabel.includes("panic") || eventLabel.includes("button")) {
            return VideoLabelIcon.Alarm;
        }

        if (eventLabel.includes("user")) {
            return VideoLabelIcon.Driver;
        }

        if (eventLabel.includes("turning")) {
            return VideoLabelIcon.Turning;
        }

        if (eventLabel.includes("covered")) {
            return VideoLabelIcon.CameraCovered;
        }

        if (eventLabel.includes("speed")) {
            return VideoLabelIcon.Car;
        }

        if (eventLabel.includes("braking") || eventLabel.includes("acceleration")) {
            return VideoLabelIcon.Acceleration;
        }

        if (eventLabel.includes("shock")) {
            return VideoLabelIcon.Shock;
        }

        if (eventLabel.includes("absent")) {
            return VideoLabelIcon.DriverAbsent;
        }

        return VideoLabelIcon.Video;
    }

    downloadVideo(video: IVideoEntry): void {
        const fileUrls = video.channelMedia.map(media => media.mp4VideoUrl);

        fileUrls.forEach((url: string) => {
            const iframe: HTMLIFrameElement = document.createElement("iframe");
            iframe.setAttribute('src', url);
            document.body.appendChild(iframe);

            setTimeout(() => {
                iframe.remove();
            }, 10000);
        });
    }

    async getPeripherals(vehicleId: number, invalidateCache = false): Promise<IPeripheral[]> {
        const options = getOptionsQueryParams({ minsToLive: 60, invalidateCache });

        const result = await lastValueFrom(this.httpClient
            .get<IPeripheral[]>(`/api/cameras/peripherals/${vehicleId}`, options));

        const products = await this.productService.getProductUiViewModels();

        return result.map(peripheral => {
            const product = products.find(p => p.ProductId == peripheral.ProductId);
            const isLiveStreamCam = peripheral.Product?.ProductMetadata?.CameraFeatures?.LiveStream;

            return { ...peripheral, Product: product, isLiveStreamCam };
        });
    }

    async getLiveStreamUrls(vehicleId: number, options?: CameraHistoricalStreamOptions): Promise<IVideoStream[]> {
        return await lastValueFrom(this.httpClient
            .get<IVideoStream[]>(`/api/cameras/streams/${vehicleId}/live`, getOptionsQueryParams(options)));
    }

    filterDuplicateVideoStreams(videoStreams: IVideoStream[] = []): IVideoStream[] {
        const uniqueStreams: IVideoStream[] = [];

        videoStreams?.forEach(videoStream => {
            if (uniqueStreams.find(uniqueStream => uniqueStream.ChannelId === videoStream.ChannelId && uniqueStream.StreamUrl === videoStream.StreamUrl)) {
                return;
            }

            uniqueStreams.push(videoStream);
        });

        return uniqueStreams;
    }

    async getPeripheralMappings(peripheralSerial: string, invalidateCache = false): Promise<IVehiclePeripheralMapping> {
        const options = getOptionsQueryParams({ minsToLive: 60, invalidateCache });

        return await lastValueFrom(this.httpClient
            .get<IVehiclePeripheralMapping>(`/api/cameras/peripheral/mapping/current/${peripheralSerial}`, options));
    }

    async getCameraChannels(queryOptions: IVideoChannelQueryOptions = {}): Promise<ICameraChannel[]> {
        const options = getOptionsQueryParams(queryOptions);

        return await lastValueFrom(this.httpClient
            .get<ICameraChannel[]>("/api/cameras/channels", options))
    }

    async videoRestrictionCheck(videoRequest: IVideoRequest): Promise<IVideoRequestRestrictionResponse> {
        return await lastValueFrom(this.httpClient
            .post<IVideoRequestRestrictionResponse>("/api/cameras/videoRestrictionCheck", videoRequest));
    }

    async closeChannelStreams(serial: string): Promise<void> {
        return await lastValueFrom(this.httpClient
            .post<void>("/api/cameras/streams/close", { serial: serial }));
    }

    async peripheralHasActiveSessions(serial: string): Promise<boolean> {
        const options = getOptionsQueryParams({ serial: serial, IsActive: true });

        return await lastValueFrom(this.httpClient
            .get<boolean>("/api/cameras/streams/currentlyopen", options));
    }

    async switchStreamType(serial: string, streamType: FtCloudStreamType): Promise<void> {
        return await lastValueFrom(this.httpClient
            .put<void>("/api/cameras/streams/switchtype", { serial, streamType }))
    }

    getPrimaryChannel(streams: IVideoStream[], totalStreams: number = streams.length): IVideoStream {
        const forwardStream = streams.find(stream => stream.CameraLocation === CameraLocation.Forward);

        if (forwardStream) {
            return forwardStream;
        }

        if (totalStreams >= 5) {
            const channel5Stream = streams.find(s => s.ChannelId === 5);

            if (channel5Stream) {
                return channel5Stream;
            }
        }

        const channel1Stream = streams.find(s => s.ChannelId === 1);
        if (channel1Stream) {
            return channel1Stream;
        }

        return streams.firstOrDefault();
    }

    getFilteredEventsForVideo(events: IPlotEvent[]): IPlotEvent[] {
        return events
            .filter(event => event.video || event.label == "Start" || event.label == "Stop" || event.label == "Current Position")
            .map(event => {
                if (event.video && !event.video.icon) {
                    event.video.icon = this.getIconForEvent(event.video.displayEventCode);
                }

                return event;
            });
    }

    getVehiclesWithMetadataUpdated(vehicles: IVehicle[], videoEntries: IVideoEntry[]): IVehicle[] {
        return vehicles.map(vehicle => {
            const videoEvents = videoEntries
                .filter(videoEntry => videoEntry.VehicleId === vehicle.Id)
                .sort((a, b) => this.dateService.sort(a.VideoStartTime, b.VideoStartTime));

            const needsReviewCount = vehicle
                .videoEvents
                ?.filter(videoEvent => videoEvent.MetaData.Authorised.length === 0)
                .length;

            const unwatchedCount = vehicle
                .videoEvents
                ?.filter(videoEvent => videoEvent.MetaData.Watched.length === 0 && videoEvent.Status === VideoStatus.Downloaded)
                .length;

            return { ...vehicle, videoEvents, needsReviewCount, unwatchedCount };
        });
    }

    getVehiclesWithStreamingChannelsMapped(channels: ICameraChannel[], vehicles: IVehicle[]): IVehicle[] {
        if (!channels?.length || !vehicles?.length) {
            return vehicles || [];
        }

        return vehicles.map(vehicle => {
            const videoChannels = channels.filter(channel => channel.VehicleId === vehicle.Id);
            return { ...vehicle, videoChannels };
        });
    }

    async getPlaybackUrls(vehicleId: number, options: CameraHistoricalStreamOptions): Promise<IVideoStream[]> {
        const result = await lastValueFrom(this.httpClient
            .get<IVideoStream[]>(`/api/cameras/streams/${vehicleId}/historical`, getOptionsQueryParams(options)));

        result.forEach(channel => {
            if (channel.StreamUrl) {
                channel.StreamUrl = channel.StreamUrl.replace("substream", "sub").replace(":9100", ":28090");
            }
        })

        return result;
    }

    async playbackSeek(playSession: string, startTime: Date): Promise<IVideoStream[]> {
        const result = await lastValueFrom(this.httpClient
            .put<IVideoStream[]>(`/api/ftcloudapi/playback/seek`, { playSession: playSession, seekTime: startTime.toISOString() }));

        return result;
    }

    async playbackPause(playSession: string): Promise<IVideoStream[]> {
        const result = await lastValueFrom(this.httpClient
            .put<IVideoStream[]>(`/api/ftcloudapi/playback/pause`, { playSession: playSession }));

        return result;
    }

    async playbackResume(playSession: string): Promise<IVideoStream[]> {
        const result = await lastValueFrom(this.httpClient
            .put<IVideoStream[]>(`/api/ftcloudapi/playback/resume`, { playSession: playSession }));

        return result;
    }

    getCameraThresholds(): CameraEventThresholds {
        const acceleration = new CameraEventThreshold(0.3, 0.4, 0.6);
        const braking = new CameraEventThreshold(0.4, 0.5, 0.7);
        const cornering = new CameraEventThreshold(0.4, 0.5, 0.6);

        return new CameraEventThresholds(acceleration, braking, cornering);
    }

    getRegionsOfAccelerometerActivity(data: IVideoExtensionData): ICameraEventPeriod[] {
        if (!data || data.VideoAccelerometerData?.length == 0) {
            return null;
        }

        const thresholds = this.getCameraThresholds();
        const eventPeriods: ICameraEventPeriod[] = [];
        const startDate = data.VideoAccelerometerData[0].Timestamp;

        let currentPeriod: ICameraEventPeriod;

        data.VideoAccelerometerData.forEach(data => {
            const hasBreached =
                Math.abs(data.Y) >= thresholds.cornering.medium ||
                Math.abs(data.X) >= thresholds.acceleration.medium ||
                Math.abs(data.X) >= thresholds.braking.medium;

            if (hasBreached) {
                if (!currentPeriod) {
                    currentPeriod = {
                        start: data.Timestamp,
                        startSecond: this.dateService.getDiff(data.Timestamp, startDate, DateUnit.Sec),
                        description: "Accel. Activity"
                    };

                    eventPeriods.push(currentPeriod);
                }

                currentPeriod.end = data.Timestamp;
                currentPeriod.endSecond = this.dateService.getDiff(currentPeriod.end, startDate, DateUnit.Sec);
            }
            else if (currentPeriod) {
                currentPeriod = null;
            }
        });

        return eventPeriods;
    }
}
